'use client';

import Link from 'next/link';
import { useLayoutEffect, useRef, useState } from 'react';

import { FreeToolIcon } from '@/components/free-tools/tool-icon';
import { trackFreeToolCtaClick } from '@/components/free-tools/track-free-tool-cta';
import type { NavLinkItem, NavMenuGroup } from '@/lib/marketing-nav';
import { cn } from '@/lib/utils';

/**
 * Grouped panel width. Applied as an inline style AND used by the flip math, so
 * the two can never disagree.
 *
 * "Free Tools" is the last nav item, so panel width decides how wide a screen
 * has to be before it can open rightward at all. 50rem opens rightward from
 * roughly 1940px and flips cleanly below that, never overflowing.
 */
const GROUPED_PANEL_WIDTH_PX = 800; // 50rem

/** Floor for the measured width, so a cramped viewport still gets a usable panel. */
const MIN_GROUPED_PANEL_WIDTH_PX = 280;

/** A group larger than this takes two of the four columns and splits its items. */
const WIDE_GROUP_ITEM_THRESHOLD = 8;

const VIEWPORT_GUTTER_PX = 16;

interface NavMenuDropdownProps {
  label: string;
  /** Single-column mode. Mutually exclusive with `groups`. */
  items?: NavLinkItem[];
  /** Grouped multi-column mode, for menus too long to scan as one list. */
  groups?: NavMenuGroup[];
  hubLink?: NavLinkItem;
  ariaLabel?: string;
  /**
   * Which edge of the panel anchors to the trigger. Grouped menus open rightward
   * by default and only flip when there is not enough room, so this is the
   * fallback rather than a fixed choice.
   */
  align?: 'left' | 'right';
  /** Width override. Clamp to the viewport, e.g. `w-[min(52rem,calc(100vw-2rem))]`. */
  panelClassName?: string;
}

export function NavMenuDropdown({
  label,
  items,
  groups,
  hubLink,
  ariaLabel,
  align = 'left',
  panelClassName,
}: NavMenuDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isGrouped = Boolean(groups && groups.length > 0);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [flipped, setFlipped] = useState(false);
  const [panelWidth, setPanelWidth] = useState<number | null>(null);

  /**
   * A grouped panel opens rightward from the trigger, which reads naturally.
   * "Free Tools" is the last nav item though, so on narrower screens there is
   * not enough room and it has to flip to right-anchored.
   *
   * Flipping alone is not enough: a right-anchored panel wider than the space
   * to the LEFT of the trigger runs off that edge instead (it did, by 30px at
   * 768). So measure both sides and size the panel to whichever one it will
   * actually occupy.
   */
  useLayoutEffect(() => {
    if (!isOpen || !isGrouped) return;

    const measure = () => {
      const el = triggerRef.current;

      if (!el) return;
      const { left, right } = el.getBoundingClientRect();
      const spaceRight = window.innerWidth - left - VIEWPORT_GUTTER_PX;
      const spaceLeft = right - VIEWPORT_GUTTER_PX;
      const shouldFlip = spaceRight < GROUPED_PANEL_WIDTH_PX;
      setFlipped(shouldFlip);
      setPanelWidth(
        Math.max(
          MIN_GROUPED_PANEL_WIDTH_PX,
          Math.min(GROUPED_PANEL_WIDTH_PX, shouldFlip ? spaceLeft : spaceRight)
        )
      );
    };

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, [isOpen, isGrouped]);

  const anchor = isGrouped ? (flipped ? 'right' : 'left') : align;

  return (
    <div
      ref={triggerRef}
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        className="text-base font-bold text-white/90 hover:text-white transition-colors"
        // Grouped mode is headings + links, not a menu. Screen readers handle that
        // better as a labelled group than as a fake menu widget.
        aria-haspopup={isGrouped ? undefined : 'menu'}
        aria-expanded={isOpen}
        aria-label={ariaLabel ?? `${label} menu`}
        onClick={() => setIsOpen((prev) => !prev)}
        onFocus={() => setIsOpen(true)}
      >
        {label}
      </button>
      <div
        style={
          isGrouped
            ? {
                // `panelWidth` is measured on open; the constant is only the
                // pre-measurement fallback for the very first paint.
                width: panelWidth ?? GROUPED_PANEL_WIDTH_PX,
                maxWidth: 'calc(100vw - 2rem)',
              }
            : undefined
        }
        // No `mt-*`: a margin here creates a dead zone that closes the menu when
        // the pointer travels from the trigger to the panel. Pad inside instead.
        className={cn(
          'absolute top-full z-50 rounded-xl border border-white/20 bg-black p-2 shadow-xl transition-all duration-150',
          // Cap the panel well short of the window. Left uncapped, the longest
          // column (Download Media, 10 items) made this ~1300px tall and it read
          // as a full-page takeover rather than a menu.
          isGrouped
            ? 'max-h-[70vh] overflow-y-auto overscroll-contain'
            : 'max-h-[80vh] overflow-y-auto',
          anchor === 'right' ? 'right-0' : 'left-0',
          // Grouped width is an inline style, not a class: it shares one
          // constant with the flip calculation below, so the two can never
          // disagree about how wide the panel actually is.
          isGrouped ? undefined : (panelClassName ?? 'w-fit min-w-[14rem] max-w-xs'),
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
        role={isGrouped ? 'group' : undefined}
        aria-label={isGrouped ? ariaLabel ?? label : undefined}
      >
        {isGrouped ? (
          // Four columns across three groups: the outsized group takes two of
          // them and lays its own items out in two, which is what actually
          // shortens the panel. Three equal columns left the tallest at 10 rows.
          // Only at lg though: four columns in a 768px panel gives each one
          // ~171px, too narrow for the descriptions to read.
          <div className="grid grid-cols-1 gap-x-2 gap-y-4 p-1 md:grid-cols-2 lg:grid-cols-4">
            {groups!.map((group) => {
              const isWide = group.items.length > WIDE_GROUP_ITEM_THRESHOLD;

              return (
              <div key={group.heading} className={cn(isWide && 'md:col-span-2')}>
                <h3 className="px-3 pb-1 pt-2 text-xs font-bold uppercase tracking-wide text-white/50">
                  {group.heading}
                </h3>
                <div className={cn(isWide && 'lg:grid lg:grid-cols-2 lg:gap-x-2')}>
                {group.items.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    prefetch={item.prefetch ?? false}
                    className="flex items-start gap-2.5 rounded-lg px-3 py-2 transition-colors hover:bg-white/10"
                    onClick={() => {
                      trackFreeToolCtaClick({
                        surface: 'nav_menu',
                        destination: item.href,
                        label: item.label,
                      });
                      setIsOpen(false);
                    }}
                  >
                    {item.iconName ? (
                      <FreeToolIcon
                        name={item.iconName}
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#EC4899]"
                      />
                    ) : null}
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-white/90">
                        {item.label}
                      </span>
                      {item.description ? (
                        <span className="mt-0.5 block text-xs leading-snug text-white/50">
                          {item.description}
                        </span>
                      ) : null}
                    </span>
                  </Link>
                ))}
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          items?.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              prefetch={item.prefetch ?? false}
              className="flex whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold text-white/90 transition-colors hover:bg-white/10 hover:text-white"
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </Link>
          ))
        )}
        {hubLink ? (
          <>
            <div className="my-1 border-t border-white/10" />
            <Link
              href={hubLink.href}
              prefetch={hubLink.prefetch ?? false}
              className="flex rounded-lg px-3 py-2 text-sm font-semibold text-[#EC4899] transition-colors hover:bg-white/10"
              onClick={() => setIsOpen(false)}
            >
              {hubLink.label}
            </Link>
          </>
        ) : null}
      </div>
    </div>
  );
}
